import {
  and,
  between,
  eq,
  inArray,
  or,
  sql,
  lte,
  isNull,
  gte,
  asc,
  isNotNull,
  gt,
  lt,
  like,
} from "drizzle-orm";

import { leiloes } from "@/db/schema";

import { removeAccents } from "@/helper/property";
import { daysFromNow } from "@/helper/date";

import { defaultTypeCriteria, todayUnixUTC, today } from "@/constants";

import type { BankCriteria, TypeCriteria } from "@/types";

const normalizeText = (text: string) => {
  return removeAccents(text.toLowerCase())
    .replace(/[^a-z0-9\s]/g, " ") // Replace special chars with space
    .trim();
};

export const paymentQuery = (payment: string[]) =>
  payment.length
    ? [
        or(
          ...(payment.includes("fgts") ? [eq(leiloes.aceita_fgts, true)] : []),
          ...(payment.includes("financiamento")
            ? [eq(leiloes.aceita_financiamento, true)]
            : []),
          ...(payment.includes("parcelamento")
            ? [eq(leiloes.aceita_parcelamento, true)]
            : []),
          ...(payment.includes("consorcio")
            ? [eq(leiloes.aceita_consorcio, true)]
            : [])
        ),
      ]
    : [];

export const typeQuery = (type: TypeCriteria) =>
  type !== defaultTypeCriteria && type ? [eq(leiloes.tipo, type)] : [];

export const modalitiesQuery = (modalities: string[]) =>
  modalities.length > 0
    ? [inArray(leiloes.modalidade_de_venda, modalities)]
    : [];

export const banksQuery = (banks: BankCriteria[]) =>
  banks.length > 0 ? [inArray(leiloes.banco, banks)] : [];

export const priceQuery = (min: number, max: number) => {
  if (min > 0) {
    return or(
      and(between(leiloes.preco1, min, max)),
      and(between(leiloes.preco2, min, max))
    );
  }

  return or(
    or(lte(leiloes.preco1, max), isNull(leiloes.preco1)),
    or(lte(leiloes.preco2, max), isNull(leiloes.preco2))
  );
};

export const areaQuery = (
  areaSize: number | null,
  propertySize: number | null
) => {
  const conditions = [];

  if (areaSize) {
    conditions.push(
      gte(leiloes.total_area, areaSize),
      gte(leiloes.total_area_terreno, areaSize)
    );
  }

  if (propertySize) {
    conditions.push(gte(leiloes.total_area_privativa, propertySize));
  }

  return conditions.length > 0 ? or(...conditions) : undefined;
};

export const citiesQuery = (cities: string[]) =>
  cities.length > 0 ? [inArray(leiloes.cidade_descricao, cities)] : [];

export const citiesLowerQuery = (cities: string[]) =>
  cities.length > 0
    ? [
        inArray(
          sql`LOWER(${leiloes.cidade_descricao})`,
          cities.map((city) => city.toLowerCase())
        ),
      ]
    : [];

export const searchTermQuery = (value: string | string[]) => {
  if (!value) return undefined;

  const terms = Array.isArray(value) ? value : [value];

  // Process all terms into a single array of normalized words
  const words = terms
    .map((term) => normalizeText(term))
    .join(" ")
    .split(/\s+/)
    .filter((word) => word.length > 0);

  if (words.length === 0) return undefined;

  // Create a simple AND query with prefix matching for each word
  const processedQuery = words.map((word) => `${word}:*`).join(" & ");

  return sql`(
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.tipo}, '')), 'A') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.cidade_descricao}, '')), 'A') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.numero_do_leilao}, '')), 'A') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.endereco}, '')), 'B') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.bairro}, '')), 'B') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.matricula}, '')), 'B') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.inscricao_imobiliaria}, '')), 'B') ||
    setweight(to_tsvector('portuguese', COALESCE(${leiloes.descricao}, '')), 'D'))
    @@ to_tsquery('portuguese', ${processedQuery})`;
};

export const todayQuery = () => {
  const formattedToday = today.toISOString().split("T")[0];
  return sql`DATE(${leiloes.criado_em}) = ${formattedToday}`;
};

export const auctionDateValidQuery = () =>
  or(
    or(
      isNull(leiloes.data_de_licitacao1),
      eq(leiloes.data_de_licitacao1, 0),
      gte(leiloes.data_de_licitacao1, todayUnixUTC)
    ),
    and(gte(leiloes.data_de_licitacao2, todayUnixUTC))
  );

export const orderActionDate = () => [
  sql`LEAST(COALESCE(${leiloes.data_de_licitacao1}, ${leiloes.data_de_licitacao2}, 0))`,
  asc(leiloes.id),
];

export const notNullComarcaQuery = () => isNotNull(leiloes.comarca);

export const nullComarcaQuery = () => isNull(leiloes.comarca);

export const notExcludedQuery = () =>
  or(eq(leiloes.excluido, false), isNull(leiloes.excluido));

export const discountQuery = (discount: number) =>
  discount > 0 ? gte(leiloes.desconto, discount) : undefined;

export const neighborhoodsQuery = (neighborhood: string[]) =>
  neighborhood.length > 0
    ? [
        or(
          ...neighborhood.map((n) =>
            like(sql`LOWER(${leiloes.bairro})`, `%${n.toLowerCase()}%`)
          )
        ),
      ]
    : [];

export const addressQuery = (address: string) =>
  address.length > 0
    ? like(sql`LOWER(${leiloes.endereco})`, `%${address.toLowerCase()}%`)
    : undefined;

export const upcomingQuery = (auctionDays1: number, auctionDays2?: number) => {
  if (auctionDays1 === 0) return undefined;

  const day1FromNow = daysFromNow(auctionDays1);

  if (!auctionDays2) {
    return or(
      and(
        gt(leiloes.data_de_licitacao1, todayUnixUTC),
        lt(leiloes.data_de_licitacao1, day1FromNow)
      ),
      and(
        gt(leiloes.data_de_licitacao2, todayUnixUTC),
        lt(leiloes.data_de_licitacao2, day1FromNow)
      )
    );
  }

  const day2FromNow = daysFromNow(auctionDays2);

  return or(
    and(
      gt(leiloes.data_de_licitacao1, todayUnixUTC),
      lt(leiloes.data_de_licitacao1, day1FromNow)
    ),
    and(
      gt(leiloes.data_de_licitacao1, todayUnixUTC),
      lt(leiloes.data_de_licitacao1, day2FromNow)
    ),
    and(
      gt(leiloes.data_de_licitacao2, todayUnixUTC),
      lt(leiloes.data_de_licitacao2, day1FromNow)
    ),
    and(
      gt(leiloes.data_de_licitacao2, todayUnixUTC),
      lt(leiloes.data_de_licitacao2, day2FromNow)
    )
  );
};
